import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCreditCard, FaCheckCircle, FaUsers, FaTrophy, FaPercentage, FaCalendarAlt, FaLock, FaSpinner, FaGem } from 'react-icons/fa';
import './BecomeMember.css';
import API_BASE_URL from '../../config/api';

const BecomeMember = ({ userData }) => {
    const navigate = useNavigate();
    const [fees, setFees] = React.useState({ memberFee: 3, minorMemberFee: 1.5, admissionFee: 5 });
    const [loading, setLoading] = React.useState(true);
    const [startingPayment, setStartingPayment] = React.useState(false);
    const [errorMsg, setErrorMsg] = React.useState(null);

    React.useEffect(() => {
        const fetchFees = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/api/fees`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setFees({
                        memberFee: data.memberFee ?? 3,
                        minorMemberFee: data.minorMemberFee ?? 1.5,
                        admissionFee: data.admissionFee !== undefined ? data.admissionFee : 5
                    });
                }
            } catch (err) {
                console.error('Error fetching fees:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchFees();
    }, []);

    const calculateAge = (birthDate) => {
        if (!birthDate) return 18;
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const isMinor = calculateAge(userData?.birthDate) < 18;
    const quotaFee = isMinor ? fees.minorMemberFee : fees.memberFee;
    const admissionFee = fees.admissionFee ?? 5;
    const totalAmount = quotaFee + admissionFee;

    const benefits = [
        {
            icon: <FaPercentage />,
            title: 'Descontos Exclusivos',
            description: 'Até 20% de desconto em eventos e atividades'
        },
        {
            icon: <FaCalendarAlt />,
            title: 'Acesso a Eventos',
            description: 'Participe em todos os eventos do clube'
        },
        {
            icon: <FaTrophy />,
            title: 'Competições',
            description: 'Inscrição prioritária em torneios'
        },
        {
            icon: <FaUsers />,
            title: 'Comunidade',
            description: 'Faça parte da família CDP'
        }
    ];

    const handlePayment = () => {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');
        if (!token) { navigate('/login'); return; }

        const now = new Date();
        navigate('/checkout', {
            state: {
                type: 'quota',
                amount: totalAmount,
                description: `Primeira Quota + Jóia de Admissão — ${isMinor ? 'Menor' : 'Adulto'} — ${now.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}`,
                selectedUserId: userId ? parseInt(userId) : null,
                buyerEmail: userData?.email || '',
                periodYear: now.getFullYear(),
                periodMonth: now.getMonth() + 1,
                successPath: '/dashboard-socio',
                cancelPath: '/dashboard-socio',
                queryParams: '?payment=success',
            }
        });
    };

    if (loading) return null;

    return (
        <div className="become-member-container">
            {/* Welcome Section */}
            <div className="welcome-section">
                <div className="welcome-icon">
                    <FaUsers />
                </div>
                <h1>Bem-vindo, {userData.firstName}!</h1>
                <p className="welcome-text">
                    Obrigado por se registar no Clube Desportivo da Póvoa.
                    Para se tornar sócio oficial e ter acesso a todos os benefícios,
                    complete o seu registo efetuando o pagamento da primeira quota e jóia de admissão.
                </p>
            </div>

            {/* Benefits Grid */}
            <div className="benefits-section">
                <h2>Benefícios de Sócio</h2>
                <div className="benefits-grid">
                    {benefits.map((benefit, index) => (
                        <div key={index} className="benefit-card">
                            <div className="benefit-icon">{benefit.icon}</div>
                            <h3>{benefit.title}</h3>
                            <p>{benefit.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Payment Section */}
            <div className="payment-section">
                <div className="payment-container">
                    <div className="payment-header">
                        <FaCreditCard />
                        <h2>Torne-se Sócio Agora</h2>
                    </div>

                    <div className="payment-content">
                        {/* Breakdown Box */}
                        <div className="become-member-breakdown">
                            <div className="bm-breakdown-row">
                                <span className="bm-label">Quota Mensal {isMinor ? '(Menor ≤ 17 anos)' : '(Adulto)'}</span>
                                <span className="bm-val">€{quotaFee.toFixed(2)}</span>
                            </div>
                            <div className="bm-breakdown-row">
                                <span className="bm-label">
                                    Jóia de Admissão <span className="bm-badge">Taxa Única</span>
                                </span>
                                <span className="bm-val">€{admissionFee.toFixed(2)}</span>
                            </div>
                            <div className="bm-breakdown-divider" />
                            <div className="bm-breakdown-row bm-total-row">
                                <span className="bm-total-label">Total do 1º Pagamento</span>
                                <span className="bm-total-val">€{totalAmount.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="payment-info">
                            <p className="info-text">
                                Ao tornar-se sócio, terá acesso imediato ao seu cartão de sócio digital, descontos exclusivos e participação em todas as atividades do clube.
                            </p>
                        </div>

                        {errorMsg && (
                            <div className="payment-error-msg">
                                {errorMsg}
                            </div>
                        )}

                        {/* Payment method badges */}
                        <div className="stripe-payment-methods">
                            <span className="stripe-methods-label">Métodos de pagamento aceites:</span>
                            <div className="stripe-methods-icons">
                                <span className="stripe-method-badge"><FaCreditCard /> Cartão</span>
                                <span className="stripe-method-badge">MB</span>
                                <span className="stripe-method-badge">MB Way</span>
                            </div>
                        </div>

                        <button
                            className="payment-button"
                            onClick={handlePayment}
                            disabled={startingPayment}
                            id="become-member-pay-btn"
                        >
                            {startingPayment ? (
                                <>
                                    <FaSpinner className="spin-icon" />
                                    A processar...
                                </>
                            ) : (
                                <>
                                    <FaCreditCard />
                                    Pagar Primeira Quota ({totalAmount.toFixed(2)}€)
                                </>
                            )}
                        </button>

                        <div className="payment-note">
                            <FaLock style={{ marginRight: '6px', opacity: 0.6, fontSize: '0.75rem' }} />
                            <p>
                                Pagamento seguro processado via Stripe.
                                Após a confirmação do pagamento, o seu acesso será ativado automaticamente.
                            </p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default BecomeMember;
